import type { DocsLink } from '../types';

/**
 * Schemes a stored docs URL may be turned into a real anchor with.
 *
 * The list is an allowlist rather than a `javascript:` denylist on purpose:
 * these URLs are hand-typed into an admin form and then rendered as an `href`,
 * which is the textbook shape of a stored-XSS hole. Anything outside the
 * allowlist — `javascript:`, `data:`, a bare `confluence.example.com` with no
 * scheme at all — is shown as plain text instead of a link.
 */
const RENDERABLE_SCHEMES = ['http:', 'https:'];

/**
 * Can this URL safely become an `<a href>`?
 *
 * Parsing rather than string-prefix matching, so leading whitespace, mixed case
 * (`JavaScript:`) and embedded control characters can't smuggle a scheme past
 * the check the way `startsWith('https://')` can.
 */
export function isRenderableDocsUrl(url: string | undefined): boolean {
  const trimmed = url?.trim();
  if (!trimmed) return false;
  try {
    return RENDERABLE_SCHEMES.includes(new URL(trimmed).protocol);
  } catch {
    // Not a parseable absolute URL — relative paths included, which have no
    // meaning here (these point at Confluence, not at the admin app).
    return false;
  }
}

/**
 * Advisory validation for the editor. Returns a warning string to show beneath
 * the input, or `null` when the value looks fine.
 *
 * Never blocks a save: an admin mid-way through pasting a URL, or recording a
 * page reference that isn't a URL yet, should not be trapped in the form. The
 * cost of a bad value is a link that renders as text, which the warning already
 * told them to expect.
 */
export function docsUrlWarning(url: string | undefined): string | null {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  if (isRenderableDocsUrl(trimmed)) return null;
  return 'Needs a full https:// address to open as a link — saved, but shown as plain text.';
}

/**
 * Normalise an editor's working rows into what gets written to Firestore.
 *
 * Drops rows with no URL (an empty row the admin added and abandoned), trims
 * both fields, and falls back to the URL as the label when no title was given
 * so a link never renders as a nameless chip.
 */
export function cleanDocsLinks(rows: DocsLink[]): DocsLink[] {
  return rows
    .map((row) => ({ title: row.title?.trim() ?? '', url: row.url?.trim() ?? '' }))
    .filter((row) => row.url.length > 0)
    .map((row) => ({ title: row.title || row.url, url: row.url }));
}
